"use client";

// ROOM 13 · yours (black cherry, the hardest room on the page). The
// commitments, said to a person, with the mechanism as a quiet second line so a
// developer can still check.
//
// John (2026-10-20): "have them scroll horizontally as you scroll, so you see
// all of them, and then move on to the next section vertically, on a ground
// that feels punchier, harder and darker." So the room pins while its row
// walks sideways under the vertical scroll (one transform a frame, only while
// the room is on screen), then lets go. Reduced motion gets a plain row you
// move through yourself. The never-trained sentence and the closing admission
// stay word for word.

import { memo, useCallback, useEffect, useRef, useState } from "react";
import { CellIcon, PadlockCell } from "../CellIcons";
import { RoomHead, useRoom, useReduced } from "./bits";

const STANCE: { k: "hash" | "rls" | "oauth" | "lock" | "gdpr" | "circle"; t: string; d: string; m: string }[] = [
  { k: "hash", t: "never for sale",
    d: "your memory is never sold. not to advertisers, not to a partner, not to anyone. what improves innernet is anonymised and aggregated, never your words.",
    m: "no sale of personal data, ever; improvement uses anonymised aggregates" },
  { k: "rls", t: "answers only to you",
    d: "not only to people we trust. only to you. the rule lives in the database itself, so a bug in our app cannot hand your rows to someone else.",
    m: "row-level security, enforced in postgres" },
  { k: "circle", t: "private stays private",
    d: "every fact carries how far it may travel. the things you'd never say at work never reach the tool you use at work.",
    m: "disclosure classes, gated at the read surface" },
  { k: "oauth", t: "you approve each tool",
    d: "you never paste a password or a key into a chat. approve a tool the way you'd approve any app, and un-approve it just as fast.",
    m: "oauth 2.1; tokens stored hashed" },
  { k: "lock", t: "sealed where it sits",
    d: "the sensitive parts are encrypted in the database, not just on the way there. the copy at rest isn't readable either.",
    m: "column-level encryption at rest" },
  { k: "circle", t: "your words stay yours",
    d: "innernet only says you said something when you did. an ai’s note, a tool’s report and netti’s reading are kept apart, all the way to the answer.",
    m: "a voice on every fact: said · reported · inferred" },
  { k: "oauth", t: "every read leaves a receipt",
    d: "each time an app reads you, it leaves a line: which app, how many things, from which part of your life. never the facts themselves.",
    m: "read receipts; passes that end on their own" },
  { k: "gdpr", t: "leave with everything",
    d: "export everything, delete everything, on demand. no email, no retention call, no month of waiting.",
    m: "gdpr export + erase; named sub-processors, published" },
];

/** `n` is the room number on the evolved homepage; the live homepage has no
 *  numbers and passes "" */
function RoomPrivacy({ n: num = "13" }: { n?: string }) {
  const [ref, live] = useRoom<HTMLElement>();
  const secRef = useRef<HTMLElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const runRef = useRef(0);
  const reduced = useReduced();
  const [n, setN] = useState(0);
  const setRefs = useCallback((el: HTMLElement | null) => {
    secRef.current = el;
    ref(el);
  }, [ref]);

  // the walk's length: how far the row has to travel to show its last card.
  // The section is that much taller than the screen, and while it is pinned the
  // vertical scroll drives the row sideways (one transform, one read a frame).
  useEffect(() => {
    const sec = secRef.current;
    const tr = trackRef.current;
    if (!sec || !tr) return;
    const size = () => {
      const run = Math.max(0, tr.scrollWidth - window.innerWidth + 2 * Math.min(64, window.innerWidth * 0.05));
      runRef.current = run;
      sec.style.setProperty("--run", `${Math.round(run)}px`);
    };
    size();
    const ro = new ResizeObserver(size);
    ro.observe(tr);
    window.addEventListener("resize", size);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", size);
    };
  }, []);
  useEffect(() => {
    if (!live || reduced) return;
    const sec = secRef.current;
    const tr = trackRef.current;
    if (!sec || !tr) return;
    let raf = 0;
    let last = -1;
    const tick = () => {
      raf = 0;
      const r = sec.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      const p = span > 0 ? Math.min(1, Math.max(0, -r.top / span)) : 0;
      if (Math.abs(p - last) > 0.0005) {
        last = p;
        tr.style.transform = `translate3d(${(-p * runRef.current).toFixed(1)}px, 0, 0)`;
        setN(Math.min(STANCE.length - 1, Math.round(p * (STANCE.length - 1))));
      }
    };
    const on = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => {
      window.removeEventListener("scroll", on);
      cancelAnimationFrame(raf);
    };
  }, [live, reduced]);

  return (
    <section ref={setRefs} className="hm-room hm-privacy vx" data-room="privacy" aria-labelledby="hm-privacy-h" data-reduced={reduced || undefined}>
      <div className="vx-pin">
        <div className="hm-in vx-head">
          <RoomHead
            id="hm-privacy-h"
            n={num || undefined}
            kicker="yours"
            title={<>yours, in the plainest sense. <span className="hm-lock" aria-hidden><PadlockCell size={34} doubleShackle={false} /></span></>}
            dek="you are about to put the things you actually think somewhere. here is how it is kept, in plain words, with the mechanism under each one if you want to check."
          />
          <span className="vx-count" aria-hidden>
            {String(n + 1).padStart(2, "0")} / {String(STANCE.length).padStart(2, "0")}
          </span>
        </div>
        <div className="vx-rail">
          <div ref={trackRef} className="vx-track" tabIndex={0} aria-label={`${STANCE.length} commitments`}>
            {STANCE.map((s, i) => (
              <article key={`${s.k}-${i}`} className="vx-card" data-on={i === n || undefined} style={{ "--i": i } as React.CSSProperties}>
                <span className="vx-card__n">{String(i + 1).padStart(2, "0")}</span>
                <span className="vx-card__ic" aria-hidden><CellIcon symbol={s.k} size={30} /></span>
                <span className="vx-card__t">{s.t}</span>
                <span className="vx-card__d">{s.d}</span>
                <span className="vx-card__m">{s.m}</span>
              </article>
            ))}
          </div>
        </div>
        <div className="hm-in vx-foot">
          <p className="hm-honest">
            one thing we won&rsquo;t pretend: once an ai you connect reads your memory, it&rsquo;s in that ai&rsquo;s hands.
            connect the ones you trust.
          </p>
          <p className="hm-links">
            <a href="/privacy">privacy policy</a> <span aria-hidden>·</span> <a href="/security">security</a>{" "}
            <span aria-hidden>·</span> <a href="/terms">terms</a>
          </p>
        </div>
      </div>
    </section>
  );
}

export default memo(RoomPrivacy);
